import { useCallback, useState } from 'react'
import api from '../../../../../../services/api'
import { clearOrderId, getOrderId, setOrderId } from '../utils/orderStorage'

export interface OrderItem {
  id: number
  uuid: string
  quantity: number
  unitPrice: string
  discount: string
  amount: string
  promotional: boolean
  product_id: number
  order_id: number
  type: string
  promotion_id?: number
  product: {
    id: number
    name: string
    translated_name: string
    price: number
    brand_id: number
    class_id: number
    media: Array<{
      path: string
      format: string
    }>
  }
}

export interface Order {
  id: number
  uuid: string
  amount: string
  discount: number
  subtotal: string
  shipping: string
  coupon?: {
    name: string
  } | null
  items: OrderItem[]
}

interface CreateOrderPayload {
  product_id: number
  quantity: number
  brand_id: number
  class_id: number
  type?: string
  association_site?: boolean
  association_id?: number
}

interface UseOrderCartReturn {
  order: Order | null
  loading: boolean
  error: string | null
  createOrUpdateOrder: (payload: CreateOrderPayload[]) => Promise<void>
  updateOrderItems: (items: OrderItem[]) => Promise<Order>
  applyCoupon: (couponName: string) => Promise<void>
  fetchOrder: () => Promise<void>
  clearOrder: () => void
}

export const useOrderCart = (): UseOrderCartReturn => {
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchOrder = useCallback(async () => {
    const orderId = getOrderId()
    if (!orderId) {
      setOrder(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await api.post(`/order_status/${orderId}`)
      if (response.data.success) {
        setOrder(response.data.order)
      }
    } catch (err: any) {
      console.error('Error fetching order:', err)
      setError('Erro ao carregar o carrinho.')
      // Se o pedido não existe mais, limpar o localStorage
      if (err.response?.status === 404) {
        clearOrderId()
        setOrder(null)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const createOrUpdateOrder = useCallback(async (payload: CreateOrderPayload[]) => {
    setLoading(true)
    setError(null)

    try {
      const orderId = getOrderId()

      if (orderId) {
        try {
          // Se já existe um pedido, buscar os itens atuais e adicionar o novo
          const currentOrderResponse = await api.post(`/order_status/${orderId}`)
          const currentItems = currentOrderResponse.data.order.items

          // Verificar se o produto já existe no carrinho
          const existingItemIndex = currentItems.findIndex(
            (item: OrderItem) => item.product_id === payload[0].product_id
          )

          if (existingItemIndex > -1) {
            // Se existe, aumentar a quantidade
            currentItems[existingItemIndex].quantity += payload[0].quantity
          } else {
            // Se não existe, adicionar o novo item à lista existente
            currentItems.push({
              product_id: payload[0].product_id,
              quantity: payload[0].quantity,
              brand_id: payload[0].brand_id,
              class_id: payload[0].class_id,
              type: payload[0].type,
              association_site: payload[0].association_site,
              association_id: payload[0].association_id
            })
          }

          // Atualizar com PUT incluindo todos os itens
          const response = await api.put(`/order_cart/${orderId}`, { items: currentItems })
          if (response.data.success) {
            setOrder(response.data.order)
          }
        } catch (updateErr: any) {
          // Se o pedido não existe mais no backend, limpar e criar novo
          if (updateErr.response?.status === 404) {
            clearOrderId()
            const response = await api.post('/order_cart', {
              items: payload,
              association_site: payload[0].association_site,
              association_id: payload[0].association_id
            })
            if (response.data.success) {
              setOrderId(response.data.order.id)
              setOrder(response.data.order)
            }
          } else {
            throw updateErr
          }
        }
      } else {
        // Criar novo pedido
        const response = await api.post('/order_cart', {
          items: payload,
          association_site: payload[0].association_site,
          association_id: payload[0].association_id
        })
        if (response.data.success) {
          setOrderId(response.data.order.id)
          setOrder(response.data.order)
        }
      }
    } catch (err) {
      console.error('Error creating/updating order:', err)
      setError('Erro ao adicionar produto ao carrinho.')
    } finally {
      setLoading(false)
    }
  }, [])

  const updateOrderItems = useCallback(async (items: OrderItem[]) => {
    const orderId = getOrderId()
    if (!orderId) {
      const message = 'Nenhum pedido encontrado.'
      setError(message)
      throw new Error(message)
    }

    setLoading(true)
    setError(null)

    try {
      const response = await api.put(`/order_cart/${orderId}`, { items })
      const updatedOrder = response.data?.order as Order | undefined

      if (!response.data?.success || !updatedOrder) {
        const message = response.data?.message || response.data?.error || 'Erro ao atualizar o carrinho.'
        setError(message)
        throw new Error(message)
      }

      setOrder(updatedOrder)
      return updatedOrder
    } catch (err: any) {
      console.error('Error updating order items:', err)
      const message =
        err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Erro ao atualizar o carrinho.'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const applyCoupon = useCallback(
    async (couponName: string) => {
      const orderId = getOrderId()
      if (!orderId || !order) {
        setError('Nenhum pedido encontrado.')
        return
      }

      const normalizedCouponName = couponName.trim()
      if (!normalizedCouponName) {
        setError('Informe um cupom válido.')
        return
      }

      setLoading(true)
      setError(null)

      try {
        const validationPayload = {
          coupon: { name: normalizedCouponName },
          items: order.items.map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity
          }))
        }

        const validationResponse = await api.post('/coupon/valid-items', validationPayload)
        const validationData = validationResponse.data

        if (!validationData?.success && !validationData?.sucess) {
          throw new Error(validationData?.message || 'Cupom inválido')
        }

        const updatedItemsSource = validationData?.items || order.items
        const updatePayload: any = {
          items: updatedItemsSource.map((item: any) => ({
            product_id: item?.product_id || item?.id,
            quantity: item?.quantity || 1
          })),
          coupon: { name: validationData?.coupon?.name || normalizedCouponName }
        }

        if (validationData?.shippingDiscount !== undefined && validationData?.shippingDiscount !== null) {
          updatePayload.shippingDiscount = Number(validationData.shippingDiscount)
        }

        if (order.items?.[0]?.product?.brand_id) {
          updatePayload.brand_id = order.items[0].product.brand_id
        }

        const response = await api.put(`/order_cart/${orderId}`, updatePayload)
        if (response.data.success) {
          setOrder(response.data.order)
          return
        }

        throw new Error(response.data?.message || 'Não foi possível aplicar o cupom')
      } catch (err: any) {
        console.error('Error applying coupon:', err)
        setError(err?.response?.data?.message || err?.message || 'Erro ao aplicar cupom.')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [order]
  )

  const clearOrder = useCallback(() => {
    clearOrderId()
    setOrder(null)
  }, [])

  return {
    order,
    loading,
    error,
    createOrUpdateOrder,
    updateOrderItems,
    applyCoupon,
    fetchOrder,
    clearOrder
  }
}
